import React, { useState, useRef, useEffect } from "react";
import { Download, X } from "lucide-react";

function ModalQrComponents({ openModal, loading, handleCloseModal, localSelected }) {
    const [qrUrl, setQrUrl] = useState(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        if (localSelected?.codigo_qr_base_64) {
            // Decodifica el QR y crea un Blob
            const svgBase64 = localSelected.codigo_qr_base_64;
            const svgDecoded = atob(svgBase64);
            const svgBlob = new Blob([svgDecoded], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(svgBlob);
            setQrUrl(url);  // Actualiza el estado con la URL del QR
        }

        return () => {
            // Limpia el objeto URL cuando se cierre el modal
            if (qrUrl) {
                URL.revokeObjectURL(qrUrl);
            }
        };
    }, [localSelected?.codigo_qr_base_64, openModal]);

    const handleDescargarQr = () => {
        if (!qrUrl) return;

        try {
            // Crear una imagen a partir del SVG Blob
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                canvas.width = img.width;
                canvas.height = img.height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                const pngBase64 = canvas.toDataURL('image/png');

                // Aquí obtenemos el nombre del local
                const localName = localSelected?.buscador_razon_social || "codigo_qr";
                const safeLocalName = localName.replace(/[^a-zA-Z0-9-_]/g, "_"); // Asegura un nombre seguro para el archivo

                // Crea un enlace para descargar el PNG con el nombre del local
                const link = document.createElement('a');
                link.download = `${safeLocalName}_codigo_qr.png`; // Usa el nombre del local en el archivo
                link.href = pngBase64;
                link.click();
            };

            img.src = qrUrl;  // Establece la fuente de la imagen (SVG decodificado)
        } catch (e) {
            console.error("Error al procesar el QR:", e);
            alert("Error al generar el QR.");
        }
    };

    return (
        <>
            {openModal && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                    onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
                >
                    <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                            <h2 className="text-base font-bold text-gray-800">
                                {localSelected?.nombre_fantasia}
                            </h2>
                            <button
                                type="button"
                                aria-label="cerrar"
                                onClick={handleCloseModal}
                                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* QR preview */}
                        <div className="flex flex-col items-center px-6 py-8">
                            {qrUrl ? (
                                <div className="flex items-center justify-center rounded-xl border border-gray-100 bg-gray-50 p-4 shadow-inner">
                                    <img
                                        src={qrUrl}
                                        alt="Código QR"
                                        className="h-56 w-56 object-contain"
                                    />
                                </div>
                            ) : (
                                <div className="flex h-56 w-56 items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400">
                                    Sin código QR
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-100 px-6 py-4">
                            <button
                                type="button"
                                onClick={handleDescargarQr}
                                disabled={loading || !qrUrl}
                                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#104063] py-2.5 font-semibold text-white transition-colors hover:bg-[#0d3553] disabled:cursor-not-allowed disabled:opacity-70"
                            >
                                {loading ? (
                                    <>
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                        Descargando...
                                    </>
                                ) : (
                                    <>
                                        <Download size={18} />
                                        Descargar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Canvas oculto para convertir a PNG */}
            <canvas ref={canvasRef} className="hidden" />
        </>
    );
}

export default ModalQrComponents;
